<template lang="pug">
.loading-container.h-24(
  v-if="templateLoadingState.loading",
  v-loading="templateLoadingState.loading"
)
TwinTable(
  v-if="templateLoadingState.loaded",
  :loading="loading",
  :assets="assets",
  :asset-twin="assetTwin",
  :page="paging.page",
  :page-count="pagesTotal",
  :hide-delete="!props.editable",
  @sort="(sortOrder) => (sorting = sortOrder)",
  @row-click="onRowClick",
  @page="(page) => (paging.page = page)",
  @delete="onDelete"
)
.flex.flex-row.justify-end.mb-8(v-if="editable")
  el-button(
    v-if="!mq.mdPlus",
    type="primary",
    icon="Plus",
    circle,
    @click="openAddRelationDialog"
  ) 
  el-button(
    v-else,
    type="primary",
    icon="Plus",
    @click="openAddRelationDialog"
  ) {{ $t("addItem", { item: props.sourceTemplate.name }) }}

el-dialog(
  v-model="showAddRelationDialog",
  :title="$t('addRelation')",
  :width="mq.mdPlus ? 500 : '100%'"
)
  smx-form
    smx-form-item(:label="props.sourceTemplate.name")
      TwinSelect(
        v-model="selectedTwin",
        :query="{ templateId: props.sourceTemplate.uuid, isTemplate: false, active: true }"
      )
  .flex.flex-row.justify-end
    el-button(@click="cancelAddRelation") {{ $t("cancel") }}
    el-button(
      v-loading="addRelationLoading",
      type="primary",
      @click="addRelation"
    ) {{ $t("addRelation") }}
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { type PaginationQuery } from "../../../services/common";
import TwinTable from "./TwinTable.vue";
import {
  type LoadState,
  stateInitial,
  stateLoading,
  stateError,
  stateLoaded,
  type PaginatedLoadState,
} from "../../../models/state";
import {
  type TwinResponse,
  getTwin,
  searchTwins,
  postTwinRelation,
  deleteTwinRelation,
  type TwinQuery,
} from "../../../services/twinManagement";
import { ElNotification } from "element-plus";
import { useI18n } from "vue-i18n";
import TwinSelect from "./TwinSelect.vue";
import { useMq } from "vue3-mq";
import { useRouter } from "vue-router";
import { type TwinManagementSettings } from "@shared/components/twinManagement/types";
import { useAppStore } from "../../../stores/apps";
import { debounce } from "lodash";
import { showResponseErrorToUser } from "@shared/util/genericResponseErrors";

const mq = useMq();

const paging = ref<PaginationQuery>({
  page: 1,
  pageSize: 10,
});
watch(() => paging.value.page, loadData);

const props = defineProps<{
  target: TwinResponse;
  sourceTemplate: TwinResponse;
  direction: "from" | "to";
  editable?: boolean;
  search?: string;
}>();

const sorting = ref(null);
watch(sorting, loadData);

const templateLoadingState = ref<LoadState<TwinResponse>>({ ...stateInitial });

const assetTwin = computed(() => templateLoadingState.value.result);

const twinsLoadingState = ref<PaginatedLoadState<TwinResponse[]>>({
  ...stateInitial,
});

const assets = computed(() => twinsLoadingState.value.result ?? []);
const pagesTotal = computed(() => twinsLoadingState.value.pagesTotal ?? 0);
const loading = computed(() => twinsLoadingState.value.loading);

onMounted(() => {
  // We have to load template to show the header as well as the actual assets
  loadTemplate();
  loadData();
});

watch(() => props.target, loadData);

let abortController: AbortController | null = null;

async function loadData() {
  const params: TwinQuery = {
    isTemplate: false,
    templateId: props.sourceTemplate.uuid,
    showProperties: true,
    active: true,
    relationsWith: [props.target.uuid],
  };

  if (props.search) {
    params["search"] = props.search;
  }

  if (abortController !== null) {
    try {
      abortController.abort();
    } catch (e) {
      // noop
    }
    abortController = null;
  }

  abortController = new AbortController();

  twinsLoadingState.value = { ...twinsLoadingState.value, ...stateLoading };

  const response = await searchTwins(paging.value, params, {
    signal: abortController.signal,
  }).catch((e) => {
    twinsLoadingState.value = { ...twinsLoadingState.value, ...stateError };
    throw e;
  });

  twinsLoadingState.value = { ...stateLoaded, ...response.data };
}

const debouncedLoadData = debounce(loadData, 200);

async function loadTemplate() {
  templateLoadingState.value = { ...stateLoading };
  const response = await getTwin(props.sourceTemplate.uuid).catch((e) => {
    templateLoadingState.value = { ...stateError };
    throw e;
  });
  templateLoadingState.value = { ...stateLoaded, result: response.data };
  // NOTE: properties are sorted by date from the backend, we need sort by order
  if (templateLoadingState.value.result) {
    templateLoadingState.value.result.properties?.sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
  }
}

const router = useRouter();

const appStore = useAppStore();

function onRowClick(twin: TwinResponse) {
  const twinLinkName = appStore.getLink(
    `twin-edit.${twin.type}.${twin.templateUuid}.${twin.uuid}`,
  )?.routeName;
  router.push({
    name: twinLinkName,
    params: { template: twin.templateUuid, id: twin.uuid },
  });
}

async function onDelete(action) {
  const relation = action.relations[0];
  await deleteTwinRelation(relation.uuid);
  loadData();
}

// Add relation dialog
const showAddRelationDialog = ref(false);
function openAddRelationDialog() {
  showAddRelationDialog.value = true;
}

const selectedTwin = ref<{ uuid: string }[]>(null);

const addRelationLoading = ref(false);

const { t } = useI18n();

async function addRelation() {
  if (selectedTwin.value === null) {
    return;
  }
  addRelationLoading.value = true;
  const response = await postTwinRelation({
    fromUuid:
      props.direction === "to" ? selectedTwin.value[0].uuid : props.target.uuid,
    toUuid:
      props.direction === "to" ? props.target.uuid : selectedTwin.value[0].uuid,
    contextId: "relation",
    attributes: {},
  })
    .catch((e) => {
      showResponseErrorToUser(e, t);
      throw e;
    })
    .finally(() => {
      addRelationLoading.value = false;
    });
  showAddRelationDialog.value = false;
  selectedTwin.value = null;
  loadData();
}

function cancelAddRelation() {
  selectedTwin.value = null;
  showAddRelationDialog.value = false;
}

// Search
watch(
  () => props.search,
  (oldQuery, newQuery) => {
    if (oldQuery !== newQuery) {
      debouncedLoadData();
    }
  },
);
</script>
